/**
 * ProjectDetailPage — fullscreen project view (replaces the old modal).
 *
 * Route: /projects/:id
 * Loads the project by ID, renders a sidebar + main content layout with all
 * info sections (financial summary, OneDrive, automations, email activity).
 */
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ArrowsClockwise,
  CloudArrowUp,
  Lock,
  Pencil,
  Trash,
  Warning,
} from "@phosphor-icons/react";

import ProjectAutomationsPanel from "../components/automations/ProjectAutomationsPanel";
import { Button } from "../components/ui/button";
import NewProjectModal from "../components/projects/NewProjectModal";
import { ContractorsCard } from "../components/projects/ContractorsCard";
import { BackfillBanner, EmailActivityCard, OneDriveCard, SmartUploadCard, RoutingNotesSection, XeroSyncBanner } from "../features/projects/detail/ProjectDetailActivitySections";
import { ProjectDeleteDialog } from "../features/projects/detail/ProjectDeleteDialog";
import { ProjectFinancialSection } from "../features/projects/detail/ProjectFinancialSection";
import { SubJobsSection } from "../features/projects/detail/SubJobsSection";
import { ProjectManagementSection } from "../features/projects/detail/ProjectManagementSection";
import { OnboardingStatusSection } from "../features/projects/detail/OnboardingStatusSection";
import { ProjectDetailSidebar, StatusDropdown } from "../features/projects/detail/ProjectDetailSidebar";
import { useProjectDetailController } from "../features/projects/detail/useProjectDetailController";
import { getProject as fetchProject } from "../api/projects";
import { XERO_INVOICING_LOCKED_STATUSES, isProjectLocked } from "../data/mockProjects";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editingProject, setEditingProject] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteOneDrive, setDeleteOneDrive] = useState(false);

  const {
    project,
    setProject,
    projects,
    loading,
    error,
    msConnected,
    creatingFolder,
    isDraggingOver,
    pendingDropFiles,
    backfillDismissed,
    setBackfillDismissed,
    maxEmails,
    retryingXero,
    handleCreateFolder,
    handleBrowseFolder,
    handleDelete,
    handleFieldSave,
    handlePageDragEnter,
    handlePageDragLeave,
    handlePageDragOver,
    handlePageDrop,
    handleTriggerBackfill,
    handleRetryXeroSync,
  } = useProjectDetailController({ projectId: id, navigate });

  const handleSaveEdit = (saved) => {
    setProject(saved);
    setEditingProject(null);
  };

  const locked = project ? isProjectLocked(project.status) : false;

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-50" data-testid="project-detail-loading">
        <div className="flex items-center gap-3 text-slate-500">
          <ArrowsClockwise size={20} className="animate-spin text-primary" />
          <span className="text-sm">Loading project…</span>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 gap-3" data-testid="project-detail-error">
        <Warning size={26} className="text-red-400" />
        <p className="text-sm text-slate-600">{error || "Project not found."}</p>
        <Button size="sm" variant="outline" onClick={() => navigate("/")} data-testid="back-to-projects-btn">
          <ArrowLeft size={13} className="mr-1" /> Back to Projects
        </Button>
      </div>
    );
  }

  return (
    <div
      className="flex-1 overflow-y-auto bg-slate-50 relative"
      data-testid="project-detail-page"
      onDragEnter={handlePageDragEnter}
      onDragLeave={handlePageDragLeave}
      onDragOver={handlePageDragOver}
      onDrop={handlePageDrop}
    >
      {isDraggingOver && (
        <div className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none" data-testid="page-drop-overlay">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
          <div className="relative z-10 flex flex-col items-center gap-4 px-12 py-10 border-2 border-dashed border-violet-400 rounded-lg bg-violet-950/80 shadow-2xl max-w-sm text-center">
            <div className="w-14 h-14 rounded-full bg-violet-500/20 border border-violet-400/40 flex items-center justify-center">
              <CloudArrowUp size={28} className="text-violet-300" weight="bold" />
            </div>
            <div>
              <p className="text-lg font-bold text-white tracking-tight">Drop to Smart Upload</p>
              <p className="text-sm text-violet-300 mt-1 leading-relaxed">
                AI will sort your files into
                <br />
                <span className="font-semibold text-violet-200">{project.name}</span>
              </p>
            </div>
            <p className="text-xs text-violet-400">Drawings → drawings/ · Other files → project root</p>
          </div>
        </div>
      )}

      <div className="bg-white border-b border-slate-200 px-8 py-5 sticky top-0 z-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <button onClick={() => navigate("/")} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2" data-testid="back-to-projects-link">
              <ArrowLeft size={12} /> All Projects
            </button>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <StatusDropdown status={project.status} onSave={handleFieldSave} />
              <span className="text-[10px] text-slate-300 font-mono">#{project.id?.substring(0, 8)}</span>
            </div>
            <h1 className="font-cabinet text-2xl font-black tracking-tight text-slate-900 leading-tight">{project.name}</h1>
            {project.description && <p className="text-sm text-slate-500 mt-1.5 leading-relaxed max-w-3xl">{project.description}</p>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!XERO_INVOICING_LOCKED_STATUSES.has(project.status) && (
              <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => { setShowDeleteConfirm(true); setDeleteOneDrive(false); }} data-testid="delete-project-btn">
                <Trash size={13} className="mr-1" /> Delete
              </Button>
            )}
            {!locked && (
              <Button size="sm" className="bg-primary text-white" onClick={() => setEditingProject(project)} data-testid="edit-project-btn">
                <Pencil size={13} className="mr-1" /> Edit
              </Button>
            )}
            <Button size="sm" variant="outline" className="text-slate-700" onClick={() => navigate("/drawing-evaluation")} data-testid="open-drawing-eval-btn">
              Drawing Evaluation <ArrowRight size={13} className="ml-1" />
            </Button>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-screen-2xl mx-auto">
        <div className="lg:col-span-2 space-y-6 min-w-0">
          {locked && (
            <div className="flex items-start gap-3 bg-slate-800 text-white rounded-sm px-5 py-4" data-testid="project-locked-banner">
              <Lock size={15} className="text-slate-300 flex-shrink-0 mt-0.5" weight="fill" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">This project is Completed and locked</p>
                <p className="text-xs text-slate-300 mt-0.5">No details can be edited. Change the status above to reopen it.</p>
              </div>
            </div>
          )}

          {!backfillDismissed && (
            <BackfillBanner
              project={project}
              msConnected={msConnected}
              onTrigger={handleTriggerBackfill}
              onDismiss={() => setBackfillDismissed(true)}
            />
          )}

          <XeroSyncBanner project={project} onRetry={handleRetryXeroSync} retrying={retryingXero} />

          <div className={locked ? "pointer-events-none opacity-60 space-y-6" : "space-y-6"}>
            <ProjectFinancialSection project={project} onSave={handleFieldSave} onRefresh={async () => { const fresh = await fetchProject(project.id); setProject(fresh); }} />
            <OnboardingStatusSection project={project} />
            <SubJobsSection project={project} />
            <ProjectManagementSection project={project} />
            <ContractorsCard project={project} onSave={handleFieldSave} />
          </div>
          <OneDriveCard
            project={project}
            msConnected={msConnected}
            creatingFolder={creatingFolder}
            onCreateFolder={handleCreateFolder}
            onBrowse={handleBrowseFolder}
          />
          <SmartUploadCard project={project} msConnected={msConnected} pendingFiles={pendingDropFiles} />

          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <ProjectAutomationsPanel project={project} projects={projects} />
          </div>

          <RoutingNotesSection projectId={project.id} />
          <EmailActivityCard
            projectId={project.id}
            project={project}
            msConnected={msConnected}
            onSyncInbox={handleTriggerBackfill}
          />
        </div>

        <div className={locked ? "pointer-events-none opacity-60" : ""}>
          <ProjectDetailSidebar
            project={project}
            maxEmails={maxEmails}
            msConnected={msConnected}
            onFieldSave={handleFieldSave}
            onProjectUpdate={setProject}
            onTriggerBackfill={handleTriggerBackfill}
          />
        </div>
      </div>

      <ProjectDeleteDialog
        open={showDeleteConfirm}
        project={project}
        deleteOneDrive={deleteOneDrive}
        onDeleteOneDriveChange={setDeleteOneDrive}
        onClose={() => { setShowDeleteConfirm(false); setDeleteOneDrive(false); }}
        onConfirm={handleDelete}
      />

      {editingProject && (
        <NewProjectModal
          initialData={editingProject}
          onClose={() => setEditingProject(null)}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  );
}
